/**
 * Centralized TanStack Query key factory.
 * Structured as factories to enable surgical cache invalidation.
 *
 * Usage:
 *   queryClient.invalidateQueries({ queryKey: QUERY_KEYS.events.all() })
 *   queryClient.invalidateQueries({ queryKey: QUERY_KEYS.events.detail(id) })
 */

export const QUERY_KEYS = {
  // ── Auth ──────────────────────────────────────────────
  auth: {
    profile: () => ['auth', 'profile'],
  },

  // ── Events ────────────────────────────────────────────
  events: {
    all:    ()           => ['events'],
    lists:  ()           => ['events', 'list'],
    list:   (filters)    => ['events', 'list', filters],
    details:()           => ['events', 'detail'],
    detail: (id)         => ['events', 'detail', id],
    stats:  ()           => ['events', 'stats'],
  },

  // ── Assignments (UI term) — wraps the same cache as registrations ────────
  assignments: {
    all:     ()          => ['registrations'],
    lists:   ()          => ['registrations', 'list'],
    list:    (filters)   => ['registrations', 'list', filters],
    details: ()          => ['registrations', 'detail'],
    detail:  (id)        => ['registrations', 'detail', id],
    byEvent: (eventId)   => ['registrations', 'event', eventId],
    byUser:  (userId)    => ['registrations', 'user',  userId],
  },

  // ── Registrations — INTERNAL alias; prefer QUERY_KEYS.assignments ────────
  registrations: {
    all:    ()           => ['registrations'],
    lists:  ()           => ['registrations', 'list'],
    list:   (filters)    => ['registrations', 'list', filters],
    details:()           => ['registrations', 'detail'],
    detail: (id)         => ['registrations', 'detail', id],
    byEvent:(eventId)    => ['registrations', 'event', eventId],
  },

  // ── Reports / Analytics ───────────────────────────────
  reports: {
    all:         ()      => ['reports'],
    platform:    (range) => ['reports', 'platform-dashboard', range],
    overview:    (range) => ['reports', 'overview', range],
    events:      (range) => ['reports', 'events',   range],
    users:       (range) => ['reports', 'users',    range],
    assignments: (range) => ['reports', 'assignments', range],
    activity:    (limit) => ['reports', 'activity', limit],
    // legacy alias
    registrations: (range) => ['reports', 'assignments', range],
  },

  // ── Settings ──────────────────────────────────────────
  settings: {
    all:           ()    => ['settings'],
    profile:       ()    => ['settings', 'profile'],
    account:       ()    => ['settings', 'account'],
    organization:  ()    => ['settings', 'organization'],
    branding:      ()    => ['settings', 'branding'],
    regional:      ()    => ['settings', 'regional'],
    notifications: ()    => ['settings', 'notifications'],
    security:      ()    => ['settings', 'security'],
  },

  // ── Users (admin) ─────────────────────────────────────
  users: {
    all:     ()          => ['users'],
    lists:   ()          => ['users', 'list'],
    list:    (filters)   => ['users', 'list', filters],
    details: ()          => ['users', 'detail'],
    detail:  (id)        => ['users', 'detail', id],
  },

  // ── Notifications ─────────────────────────────────────
  notifications: {
    all:     ()          => ['notifications'],
    lists:   ()          => ['notifications', 'list'],
    list:    (filters)   => ['notifications', 'list', filters],
    mine:    (userId)    => ['notifications', 'mine', userId],
    detail:  (id)        => ['notifications', 'detail', id],
    unread:  (userId)    => ['notifications', 'unread', userId],
  },

  // ── Email Templates ───────────────────────────────────
  emailTemplates: {
    all:     ()          => ['emailTemplates'],
    lists:   ()          => ['emailTemplates', 'list'],
    list:    (filters)   => ['emailTemplates', 'list', filters],
    detail:  (id)        => ['emailTemplates', 'detail', id],
  },

  // ── Notification History ──────────────────────────────
  notificationHistory: {
    all:     ()          => ['notificationHistory'],
    list:    (filters)   => ['notificationHistory', 'list', filters],
  },

  // ── Roles & Permissions ───────────────────────────────
  roles: {
    all:     ()        => ['roles'],
    lists:   ()        => ['roles', 'list'],
    list:    (filters) => ['roles', 'list', filters],
    details: ()        => ['roles', 'detail'],
    detail:  (id)      => ['roles', 'detail', id],
  },

  // ── Connections ───────────────────────────────────────
  connections: {
    all:       ()          => ['connections'],
    lists:     ()          => ['connections', 'list'],
    list:      (filters)   => ['connections', 'list', filters],
    details:   ()          => ['connections', 'detail'],
    detail:    (id)        => ['connections', 'detail', id],
    byUser:    (userId)    => ['connections', 'user', userId],
    byEvent:   (eventId)   => ['connections', 'event', eventId],
    analytics: ()          => ['connections', 'analytics'],
  },

  // ── Outreach (Sent Emails) ────────────────────────────────
  outreach: {
    all:     ()          => ['outreach'],
    lists:   ()          => ['outreach', 'list'],
    list:    (filters)   => ['outreach', 'list', filters],
    detail:  (id)        => ['outreach', 'detail', id],
    stats:   ()          => ['outreach', 'stats'],
  },

}
